import { intlLocaleFor } from "../i18n/locales";
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Crown, KeySquare, ShieldQuestion, MessageSquare, Gauge,
  Users, CalendarClock, TrendingUp, TrendingDown, Minus, ArrowRight, Receipt,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { api } from "../lib/api";
import { fmtAmountMinor } from "../lib/usePlans";
import { useAuth } from "../context/AuthContext";
import i18n from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const PERIODS = [
  { key: "last_7d", label: "7 derniers jours" },
  { key: "last_30d", label: "30 derniers jours" },
  { key: "current_month", get label() { return tr("account:kpis.moisEnCours"); } },
  { key: "current_year", get label() { return tr("account:kpis.anneeEnCours"); } },
];

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  scheduled_change: "bg-cyan-500/15 text-cyan-600 border-cyan-500/30",
  payment_pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  payment_failed: "bg-red-500/15 text-red-600 border-red-500/30",
  suspended: "bg-red-500/15 text-red-600 border-red-500/30",
  cancellation_scheduled: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  cancelled: "bg-slate-500/15 text-slate-500 border-slate-500/30",
};

// Durée humaine (§9) : minutes / heures / jours selon l'ampleur.
const fmtDuration = (ms) => {
  if (ms == null) return "—";
  const s = ms / 1000;
  if (s < 90) return `${Math.round(s)} s`;
  const m = s / 60;
  if (m < 90) return `${Math.round(m)} min`;
  const h = m / 60;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} j`;
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(intlLocaleFor(i18n.language), { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Variation strictement informative (§12) : style NEUTRE, jamais rouge/vert.
const DeltaBadge = ({ pct }) => {
  if (pct == null) return null;
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  const sign = pct > 0 ? "+" : "";
  return (
    <span
      data-testid="kpi-delta"
      className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"
      title={tr("account:kpis.comparaisonAvecLaPeriode")}
    >
      <Icon className="w-3 h-3" />
      {sign}{pct} {tr("account:kpis.vsPrec")}
    </span>
  );
};

const KpiCard = ({ icon: Icon, title, testid, children }) => (
  <div data-testid={testid} className="glass rounded-2xl p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-emerald-600" />
      <h3 className="font-heading text-base md:text-lg font-semibold">{title}</h3>
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div>
    <div className="text-2xl font-extrabold font-heading tracking-tight text-slate-900">{value}</div>
    <div className="text-xs text-slate-500">{label}</div>
    {sub != null && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

const RevealKpi = ({ data, unitLabel }) => {
  const p = data.period;
  const l = data.lifetime;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Stat label={tr("account:kpis.v0EnvoyeEs", { v0: unitLabel })} value={p.sent} />
          </div>
          <div className="mt-1"><DeltaBadge pct={p.sent_delta_pct} /></div>
        </div>
        <div>
          <Stat label={tr("account:kpis.v0ReveleEs", { v0: unitLabel })} value={p.revealed} />
          <div className="mt-1"><DeltaBadge pct={p.revealed_delta_pct} /></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 pt-1 border-t border-slate-200">
        <Stat label={tr("account:kpis.tauxDeRevelation")} value={p.reveal_rate != null ? `${p.reveal_rate} %` : "—"} />
        <Stat label={tr("account:kpis.dureeMoyDeRevelation")} value={fmtDuration(p.avg_reveal_ms)} />
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          {tr("account:kpis.depuisLOuverture")}
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div><span className="font-semibold text-slate-900">{l.sent}</span> <span className="text-slate-400">env.</span></div>
          <div><span className="font-semibold text-slate-900">{l.revealed}</span> <span className="text-slate-400">{tr("account:kpis.rev")}</span></div>
          <div><span className="font-semibold text-slate-900">{l.reveal_rate != null ? `${l.reveal_rate}%` : "—"}</span></div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionCard = ({ s, role, onManage, showAmount }) => {
  const canManage = role === "owner" || role === "admin" || role === "billing_admin";
  return (
    <KpiCard icon={Crown} title={tr("account:kpis.abonnement")} testid="dashboard-subscription-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span data-testid="dashboard-plan-name" className="font-heading text-2xl font-extrabold">{s.plan_label}</span>
          {showAmount && s.amount_minor != null && (
            <span className="text-slate-500 text-sm">· {fmtAmountMinor(s.amount_minor, s.currency)}{tr("account:kpis.mois")}</span>
          )}
        </div>
        <Badge data-testid="dashboard-sub-status" variant="outline" className={STATUS_STYLES[s.status] || ""}>
          {tr(`account:kpis.subStatus.${s.status}`, { defaultValue: s.status_label })}
        </Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-lg font-bold text-slate-900" data-testid="dashboard-seats-purchased">{s.purchased_seats}</div>
          <div className="text-xs text-slate-500">{tr("account:kpis.siegesAchetes")}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-lg font-bold text-slate-900" data-testid="dashboard-seats-active">{s.active_members}</div>
          <div className="text-xs text-slate-500">{tr("account:kpis.membresActifs")}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-lg font-bold text-slate-900" data-testid="dashboard-seats-pending">{s.pending_invitations}</div>
          <div className="text-xs text-slate-500">{tr("account:kpis.invitations")}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-lg font-bold text-slate-900" data-testid="dashboard-seats-available">{s.available_seats}</div>
          <div className="text-xs text-slate-500">{tr("account:kpis.siegesDispo")}</div>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-4 text-sm flex-wrap">
        {s.next_billing_at && (
          <div className="flex items-center gap-1.5 text-slate-500">
            <CalendarClock className="w-4 h-4" />
            {tr("account:kpis.prochaineEcheance")} <span className="text-slate-900 font-medium">{fmtDate(s.next_billing_at)}</span>
          </div>
        )}
        {s.scheduled_change && (
          <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600 border-cyan-500/30" data-testid="dashboard-scheduled-change">
            {tr("account:kpis.changementPlanifie")}{s.scheduled_change.plan_label ? ` → ${s.scheduled_change.plan_label}` : ""}
            {s.scheduled_change.seat_count != null ? tr("account:kpis.v0Sieges", { v0: s.scheduled_change.seat_count }) : ""}
          </Badge>
        )}
        {s.cancellation_at && (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
            {tr("account:kpis.resiliationLe")} {fmtDate(s.cancellation_at)}
          </Badge>
        )}
      </div>

      {canManage ? (
        <Button
          data-testid="dashboard-manage-subscription-btn"
          onClick={onManage}
          variant="outline"
          className="mt-5 border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-900"
        >
          {tr("account:kpis.gererMonAbonnement")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      ) : (
        <p className="text-xs text-slate-400 mt-5">{tr("account:kpis.lectureSeuleContactezUn")}</p>
      )}
    </KpiCard>
  );
};

export const DashboardKPIs = ({ role, onManageSubscription }) => {
  const { activeWorkspaceId } = useAuth();
  const [period, setPeriod] = useState("current_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prefLoaded, setPrefLoaded] = useState(false);

  // Préférence période mémorisée par user × workspace (§4).
  useEffect(() => {
    let active = true;
    (async () => {
      if (!activeWorkspaceId) { setPrefLoaded(true); return; }
      try {
        const { data: prefs } = await api.get(`/account/workspaces/${activeWorkspaceId}/preferences`);
        if (active && prefs?.dashboard_period && PERIODS.some((p) => p.key === prefs.dashboard_period)) {
          setPeriod(prefs.dashboard_period);
        }
      } catch (e) { /* non bloquant */ }
      if (active) setPrefLoaded(true);
    })();
    return () => { active = false; };
  }, [activeWorkspaceId]);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/dashboard/kpis`, { params: { period: p } });
      setData(d);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (prefLoaded) load(period);
  }, [period, prefLoaded, load, activeWorkspaceId]);

  const onPeriodChange = (p) => {
    setPeriod(p);
    if (activeWorkspaceId) {
      api.put(`/account/workspaces/${activeWorkspaceId}/preferences`, { dashboard_period: p }).catch(() => {});
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const isBillingAdmin = data.view === "billing_admin";

  return (
    <div className="space-y-6" data-testid="dashboard-kpis">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">
          {tr("account:kpis.periode")} <span className="text-slate-900 font-medium">{data.period.label}</span>
        </p>
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger data-testid="dashboard-period-select" className="w-[200px] bg-white border-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.key} value={p.key} data-testid={`period-opt-${p.key}`}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <SubscriptionCard
        s={data.subscription}
        role={data.role || role}
        onManage={onManageSubscription}
        showAmount={isBillingAdmin}
      />

      {isBillingAdmin ? (
        <KpiCard icon={Receipt} title={tr("account:kpis.apercuFinancier")} testid="dashboard-billing-admin-card">
          <p className="text-sm text-slate-500 mb-4">
            {tr("account:kpis.enTantQueResponsable")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">{tr("account:kpis.moyenDePaiement")}</div>
              <div className="text-sm font-medium text-slate-900 mt-1">{tr("account:kpis.facturation")}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">{tr("account:kpis.facturesEnAttente")}</div>
              <div className="text-sm font-medium text-slate-900 mt-1">{tr("account:kpis.facturation")}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">{tr("account:kpis.montantMensuel")}</div>
              <div className="text-sm font-medium text-slate-900 mt-1">
                {data.subscription.amount_minor != null
                  ? `${fmtAmountMinor(data.subscription.amount_minor, data.subscription.currency)}`
                  : "—"}
              </div>
            </div>
          </div>
          <Button
            data-testid="billing-admin-go-billing"
            onClick={onManageSubscription}
            variant="outline"
            className="mt-5 border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-900"
          >
            {tr("account:kpis.accederALaFacturation")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </KpiCard>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <KpiCard icon={KeySquare} title={tr("account:kpis.secrets")} testid="dashboard-secrets-card">
              <RevealKpi data={data.secrets} unitLabel={tr("account:kpis.unitSecrets")} />
            </KpiCard>
            <KpiCard icon={ShieldQuestion} title={tr("account:kpis.demandesDInformations")} testid="dashboard-secure-requests-card">
              <RevealKpi data={data.secure_requests} unitLabel={tr("account:kpis.unitRequests")} />
            </KpiCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <KpiCard icon={MessageSquare} title={tr("account:kpis.sms")} testid="dashboard-sms-card">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold font-heading">{data.sms.period_consumed}</span>
                <span className="text-slate-400 text-lg">
                  / {!data.sms.available ? "non inclus" : data.sms.included == null ? "∞" : data.sms.included}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{tr("account:kpis.smsConsommesSurLa")}</div>
              <div className="text-xs text-slate-400 mt-2">
                {tr("account:kpis.depuisLOuverture2")} <span className="font-medium text-slate-600">{data.sms.lifetime_consumed}</span>
                {data.sms.allow_overage && tr("account:kpis.horsForfaitAutorise")}
              </div>
            </KpiCard>

            <KpiCard icon={Gauge} title={tr("account:kpis.quotasDuMois")} testid="dashboard-quotas-card">
              <QuotaRow label={tr("account:kpis.secrets")} q={data.quotas.secrets} />
              <div className="h-3" />
              <QuotaRow label={tr("account:kpis.demandesDInformations")} q={data.quotas.secure_requests} />
            </KpiCard>
          </div>
        </>
      )}
    </div>
  );
};

const QuotaRow = ({ label, q }) => {
  const ref = q.soft ?? q.hard;
  const pct = ref ? Math.min(100, Math.round((q.used_this_month / ref) * 100)) : 0;
  const softReached = q.soft != null && q.used_this_month >= q.soft;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono-token text-emerald-600">
          {q.used_this_month} / {ref ?? "∞"}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full transition-all ${softReached ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
      {softReached && (
        <div className="mt-1 text-xs text-amber-600">{tr("account:kpis.volumePrevuAtteintLe")}</div>
      )}
    </div>
  );
};
